import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  addSmsNumber,
  deleteSmsNumber,
  listSmsNumbers,
  retireSmsNumber,
  type SmsNumber,
} from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  PageContainer,
  Select,
  TextInput,
  toErrorMessage,
  useConfirm,
} from "@/components/Forms";

/**
 * Phase 42 — platform-owned SMS number pool. The super-admin loads from-numbers
 * the platform owns (under its own 46elks / Twilio accounts) and sees who holds
 * each. Allocation to a tenant happens on the tenant-detail page; this page is
 * inventory management: add, retire (soft), delete (free/retired only).
 */
const SMS_PROVIDERS = ["46elks", "twilio"];

export default function SmsNumbersPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [numbers, setNumbers] = useState<SmsNumber[] | null>(null);
  const [provider, setProvider] = useState(SMS_PROVIDERS[0]);
  const [e164, setE164] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listSmsNumbers()
      .then((r) => !cancelled && setNumbers(r.numbers))
      .catch((e) => !cancelled && setErr(toErrorMessage(e, t("smsNumbers.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [reloadAt, t]);

  async function add() {
    if (!e164.trim()) {
      setErr(t("smsNumbers.errNumberRequired"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await addSmsNumber({ provider, e164: e164.trim(), label: label.trim() || null });
      setE164("");
      setLabel("");
      setReloadAt((n) => n + 1);
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function retire(n: SmsNumber) {
    setErr(null);
    try {
      await retireSmsNumber(n.id);
      setReloadAt((x) => x + 1);
    } catch (e) {
      setErr(toErrorMessage(e));
    }
  }

  async function remove(n: SmsNumber) {
    const ok = await confirm({
      title: t("smsNumbers.deleteConfirmTitle"),
      body: t("smsNumbers.deleteConfirmBody", { e164: n.e164 }),
      confirmLabel: t("forms.delete"),
      destructive: true,
    });
    if (!ok) return;
    setErr(null);
    try {
      await deleteSmsNumber(n.id);
      setReloadAt((x) => x + 1);
    } catch (e) {
      setErr(toErrorMessage(e));
    }
  }

  return (
    <PageContainer title={t("smsNumbers.title")} subtitle={t("smsNumbers.subtitle")}>
      <ErrorBanner message={err} />

      <Card title={t("smsNumbers.addTitle")} description={t("smsNumbers.addDescription")}>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <Field label={t("smsNumbers.provider")}>
            <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {SMS_PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("smsNumbers.number")} required>
            <TextInput
              value={e164}
              onChange={(e) => setE164(e.target.value)}
              placeholder="+46766001122"
            />
          </Field>
          <Field label={t("smsNumbers.label")}>
            <TextInput
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t("smsNumbers.labelPlaceholder")}
            />
          </Field>
          <Button variant="primary" onClick={add} disabled={busy}>
            {busy ? t("forms.saving") : t("smsNumbers.add")}
          </Button>
        </div>
      </Card>

      <div className="h-3" />

      <Card title={t("smsNumbers.poolTitle")}>
        {numbers === null ? (
          <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
        ) : numbers.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("smsNumbers.empty")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left py-1">{t("smsNumbers.colNumber")}</th>
                <th className="text-left py-1">{t("smsNumbers.colProvider")}</th>
                <th className="text-left py-1">{t("smsNumbers.colStatus")}</th>
                <th className="text-left py-1">{t("smsNumbers.colTenant")}</th>
                <th className="text-right py-1">{t("smsNumbers.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {numbers.map((n) => (
                <tr key={n.id}>
                  <td className="py-1.5 font-mono">
                    {n.e164}
                    {n.label && (
                      <span className="block text-[11px] text-muted-foreground">{n.label}</span>
                    )}
                  </td>
                  <td className="py-1.5">{n.provider}</td>
                  <td className="py-1.5">
                    <Badge
                      tone={
                        n.status === "FREE"
                          ? "success"
                          : n.status === "ALLOCATED"
                            ? "info"
                            : "neutral"
                      }
                    >
                      {t(`smsNumbers.status.${n.status}`)}
                    </Badge>
                  </td>
                  <td className="py-1.5">
                    {n.tenantSlug ? (
                      <Link
                        to={`/tenants/${n.tenantSlug}`}
                        className="text-accent hover:underline font-mono text-xs"
                      >
                        {n.tenantSlug}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-1.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {n.status === "FREE" && (
                        <Button onClick={() => retire(n)}>{t("smsNumbers.retire")}</Button>
                      )}
                      {n.status !== "ALLOCATED" && (
                        <Button variant="destructive" onClick={() => remove(n)}>
                          {t("forms.delete")}
                        </Button>
                      )}
                      {n.status === "ALLOCATED" && (
                        <span className="text-xs text-muted-foreground">
                          {t("smsNumbers.allocatedHint")}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </PageContainer>
  );
}
